import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

// Import sub-components
import {
  EstimateHeader,
  EstimateActions,
  PropertyInfo,
  CustomerInfo,
  PricingBreakdown,
  EstimateStatus,
  ReviewRequestModal,
  SendProposalModal,
  DiscountModal,
  EagleviewImportModal,
  RoofrImportModal,
  AdditionalChargesModal,
  DetailedReport
} from './estimate-detail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EstimateDetail = () => {
  const { estimateId } = useParams();
  const { user } = useAuth();
  
  // Core state
  const [estimate, setEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Modal states
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showMeasurementOverride, setShowMeasurementOverride] = useState(false);
  const [showEagleviewImport, setShowEagleviewImport] = useState(false);
  const [showRoofrImport, setShowRoofrImport] = useState(false);
  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
  const [showReviewRequestModal, setShowReviewRequestModal] = useState(false);
  const [showLowConfidenceWarning, setShowLowConfidenceWarning] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  
  // Form states
  const [discountPercent, setDiscountPercent] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  
  // Edit customer form state
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editAddress, setEditAddress] = useState('');
  
  // Review request state
  const [isRequestingReview, setIsRequestingReview] = useState(false);
  const [reviewRequestSent, setReviewRequestSent] = useState(false);
  
  // Follow-up sequence state
  const [sequenceStatus, setSequenceStatus] = useState(null);
  const [isStartingSequence, setIsStartingSequence] = useState(false);
  
  // Measurement override state
  const [overrideSquareFootage, setOverrideSquareFootage] = useState('');
  const [overrideNotes, setOverrideNotes] = useState('');
  const [overrideVerified, setOverrideVerified] = useState(false);
  
  // Low confidence action state
  const [pendingAction, setPendingAction] = useState(null);

  // File refs
  const fileInputRef = useRef(null);
  const roofrFileInputRef = useRef(null);

  // ===================
  // Data Loading
  // ===================
  
  useEffect(() => {
    loadEstimate();
    loadSequenceStatus();
  }, [estimateId]);

  const loadEstimate = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/estimates/${estimateId}`);
      setEstimate(response.data);
      
      // Pre-fill customer info if available
      if (response.data.customer_email) {
        setCustomerEmail(response.data.customer_email);
      }
      if (response.data.customer_first_name || response.data.customer_last_name) {
        setCustomerName(`${response.data.customer_first_name || ''} ${response.data.customer_last_name || ''}`.trim());
      }
    } catch (error) {
      console.error('Error loading estimate:', error);
      toast.error('Failed to load estimate');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSequenceStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/unified-funnels/sequence-status/${estimateId}`);
      setSequenceStatus(response.data);
    } catch (error) {
      console.error('Error loading sequence status:', error);
    }
  };

  // ===================
  // Utility Functions
  // ===================
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const isLowConfidence = estimate?.measurement_confidence?.score < 70;

  // ===================
  // Protected Action Handler (Low Confidence Warning)
  // ===================
  
  const handleProtectedAction = (actionType, actionFn) => {
    if (isLowConfidence && !estimate?.measurement_verified) {
      setPendingAction({ type: actionType, fn: actionFn });
      setShowLowConfidenceWarning(true);
    } else {
      actionFn();
    }
  };

  const proceedWithAction = () => {
    if (pendingAction?.fn) {
      pendingAction.fn();
    }
    setShowLowConfidenceWarning(false);
    setPendingAction(null);
  };

  // ===================
  // Action Handlers
  // ===================
  
  const handleSendToCustomer = async () => {
    if (!customerEmail || !customerName) {
      toast.error('Please provide customer name and email');
      return;
    }

    setIsProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/customer-portal/send-proposal-link`, {
        estimate_id: estimateId,
        customer_email: customerEmail,
        customer_name: customerName
      });
      
      toast.success(`Proposal sent to ${customerEmail}!`);
      setShowSendModal(false);
      loadEstimate();
    } catch (error) {
      console.error('Error sending proposal:', error);
      toast.error('Failed to send proposal');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadPDF = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/estimates/${estimateId}/generate-pdf`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `estimate-${estimateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGenerateContract = async () => {
    // Pre-check customer email
    if (!estimate?.customer_email) {
      toast.error('Customer email is required to generate a contract. Add it in Customer Information.');
      return;
    }
    
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/estimates/${estimateId}/generate-contract`);
      
      if (response.data.signing_url) {
        toast.success('Contract generated and sent for signing!');
        window.open(response.data.signing_url, '_blank');
      } else if (response.data.contract_url) {
        window.open(response.data.contract_url, '_blank');
        toast.success('Contract generated successfully');
      } else {
        toast.success(response.data.message || 'Contract generated successfully');
      }
      loadEstimate();
    } catch (error) {
      console.error('Error generating contract:', error);
      const detail = error.response?.data?.detail || '';
      if (detail.includes('email')) {
        toast.error('Customer email is required. Please add it in Customer Information first.');
      } else if (detail.includes('DocuSeal') || detail.includes('docuseal')) {
        toast.error('Contract service error. Please check your DocuSeal configuration.');
      } else {
        toast.error(detail || 'Failed to generate contract. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountPercent || parseFloat(discountPercent) <= 0) {
      toast.error('Please enter a valid discount percentage');
      return;
    }

    setIsProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/estimates/${estimateId}/apply-discount`, {
        discount_percent: parseFloat(discountPercent)
      });
      
      toast.success(`${discountPercent}% discount applied`);
      setShowDiscountModal(false);
      setDiscountPercent('');
      loadEstimate();
    } catch (error) {
      console.error('Error applying discount:', error);
      toast.error('Failed to apply discount');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendForSigning = async () => {
    setIsProcessing(true);
    try {
      // Check customer email is present
      if (!estimate?.customer_email) {
        toast.error('Customer email is required to send for signing. Add it in Customer Information.');
        setIsProcessing(false);
        return;
      }

      const response = await axios.post(`${BACKEND_URL}/api/estimates/${estimateId}/send-for-signing`);
      
      if (response.data.signing_url) {
        toast.success('Contract sent for signing! Share the signing link with your customer.');
      }
      loadEstimate();
    } catch (error) {
      console.error('Error sending for signing:', error);
      toast.error(error.response?.data?.detail || 'Failed to send for signing');
    } finally {
      setIsProcessing(false);    }
  };

  const handleSendWarranty = async () => {
    if (!estimate?.customer_email) {
      toast.error('Customer email is required to send the warranty. Add it in Customer Information.');
      return;
    }
    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/warranty/send/estimate/${estimateId}`
      );
      if (response.data?.success) {
        toast.success(`Workmanship warranty emailed to ${response.data.customer_email}`);
      } else {
        toast.warning(
          `Warranty generated but email status: ${response.data?.email_status || 'unknown'}`
        );
      }
    } catch (error) {
      console.error('Error sending warranty:', error);
      toast.error(error.response?.data?.detail || 'Failed to send warranty');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRequestReview = async () => {
    const customerFullName = estimate?.customer_name || 
      `${estimate?.customer_first_name || ''} ${estimate?.customer_last_name || ''}`.trim();
    
    if (!estimate?.customer_email || !customerFullName) {
      toast.error('Customer name and email are required');
      return;
    }
    
    setIsRequestingReview(true);
    try {
      const token = localStorage.getItem('rooftrack_token');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/reviews/campaigns/start`,
        {
          project_id: estimateId,
          estimate_id: estimateId,
          customer_name: customerFullName,
          customer_email: estimate.customer_email,
          customer_phone: estimate.customer_phone || null,
          property_address: estimate.address || 'Property Address'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setReviewRequestSent(true);
        setShowReviewRequestModal(false);
        toast.success('Review request campaign started!');
      }
    } catch (error) {
      console.error('Error requesting review:', error);
      toast.error('Failed to start review campaign');
    } finally {
      setIsRequestingReview(false);
    }
  };

  const handleMeasurementOverride = async () => {
    if (!overrideSquareFootage || parseFloat(overrideSquareFootage) <= 0) {
      toast.error('Please enter a valid square footage');
      return;
    }
    
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/estimates/${estimateId}/override-measurement`, {
        square_footage: parseFloat(overrideSquareFootage),
        notes: overrideNotes,
        verified: overrideVerified
      });
      
      toast.success(`Measurement updated to ${overrideSquareFootage} sq ft. New total: $${response.data.new_total?.toLocaleString()}`);
      setShowMeasurementOverride(false);
      setOverrideSquareFootage('');
      setOverrideNotes('');
      setOverrideVerified(false);
      loadEstimate();
    } catch (error) {
      console.error('Error overriding measurement:', error);
      toast.error('Failed to update measurement');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRevertAdditionalCharges = async () => {
    if (!window.confirm('Are you sure you want to remove all additional charges?')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/estimates/${estimateId}/revert-additional-charges`);
      toast.success('Additional charges removed');
      loadEstimate();
    } catch (error) {
      console.error('Error reverting charges:', error);
      toast.error('Failed to revert charges');
    } finally {
      setIsProcessing(false);
    }
  };

  const openEditCustomerModal = () => {
    setEditFirstName(estimate?.customer_first_name || '');
    setEditLastName(estimate?.customer_last_name || '');
    setEditEmail(estimate?.customer_email || '');
    setEditPhone(estimate?.customer_phone || '');
    setEditAddress(estimate?.address || '');
    setShowEditCustomerModal(true);
  };

  const handleSaveCustomerInfo = async () => {
    setIsProcessing(true);
    try {
      await axios.put(`${BACKEND_URL}/api/estimates/${estimateId}/customer-info`, {
        first_name: editFirstName,
        last_name: editLastName,
        email: editEmail,
        phone: editPhone,
        address: editAddress
      });
      toast.success('Customer info updated');
      setShowEditCustomerModal(false);
      loadEstimate();
    } catch (error) {
      console.error('Error updating customer info:', error);
      toast.error('Failed to update customer info');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStartSequence = async () => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }
    setIsStartingSequence(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/unified-funnels/start-sequence/${estimateId}`, {
        user_id: user.id
      });
      if (resp.data.first_email_sent) {
        toast.success('Follow-up sequence started — first email sent immediately!');
      } else {
        toast.success('Follow-up sequence started!');
      }
      await loadSequenceStatus();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start sequence';
      toast.error(msg);
    } finally {
      setIsStartingSequence(false);
    }
  };

  const handleStopSequence = async () => {
    if (!window.confirm('Stop the follow-up sequence? Remaining scheduled messages will be cancelled.')) {
      return;
    }
    setIsProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/unified-funnels/stop-sequence/${estimateId}`);
      toast.success('Follow-up sequence stopped');
      await loadSequenceStatus();
    } catch (error) {
      toast.error('Failed to stop sequence');
    } finally {
      setIsProcessing(false);
    }
  };

  // ===================
  // Render
  // ===================
  
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading estimate...</p>
        </div>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">Estimate not found</p>
          <Link to="/dashboard/estimates">
            <Button className="mt-4">Back to Estimates</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <EstimateHeader 
        estimate={estimate} 
        estimateId={estimateId} 
      />

      {/* Actions */}
      <EstimateActions
        estimate={estimate}
        isProcessing={isProcessing}
        reviewRequestSent={reviewRequestSent}
        isLowConfidence={isLowConfidence}
        sequenceStatus={sequenceStatus}
        isStartingSequence={isStartingSequence}
        onSendToCustomer={() => setShowSendModal(true)}
        onDownloadPDF={handleDownloadPDF}
        onGenerateContract={handleGenerateContract}
        onApplyDiscount={() => setShowDiscountModal(true)}
        onSendForSigning={handleSendForSigning}
        onSendWarranty={handleSendWarranty}
        onRequestReview={() => setShowReviewRequestModal(true)}
        onGetProfessionalMeasurement={() => window.open('https://eagleview.com', '_blank')}
        onImportRoofr={() => setShowRoofrImport(true)}
        onImportEagleview={() => setShowEagleviewImport(true)}
        onStartSequence={handleStartSequence}
        onStopSequence={handleStopSequence}
        handleProtectedAction={handleProtectedAction}
      />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Property & Customer Info */}
        <div className="lg:col-span-2 space-y-6">
          <PropertyInfo
            estimate={estimate}
            onImportRoofr={() => setShowRoofrImport(true)}
            onImportEagleview={() => setShowEagleviewImport(true)}
            onOverrideMeasurement={() => setShowMeasurementOverride(true)}
          />
          
          <CustomerInfo estimate={estimate} onEdit={openEditCustomerModal} />
          
          <PricingBreakdown
            estimate={estimate}
            formatCurrency={formatCurrency}
            onAddCharges={() => setShowAdditionalCharges(true)}
            onRevertCharges={handleRevertAdditionalCharges}
          />
          
          <DetailedReport estimate={estimate} user={user} />
        </div>

        {/* Right Column - Status & Timeline */}
        <div>
          <EstimateStatus estimate={estimate} />
        </div>
      </div>

      {/* Modals */}
      <SendProposalModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        customerEmail={customerEmail}
        setCustomerEmail={setCustomerEmail}
        customerName={customerName}
        setCustomerName={setCustomerName}
        isProcessing={isProcessing}
        onSend={handleSendToCustomer}
      />

      <DiscountModal
        isOpen={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        discountPercent={discountPercent}
        setDiscountPercent={setDiscountPercent}
        isProcessing={isProcessing}
        onApply={handleApplyDiscount}
      />

      <ReviewRequestModal
        isOpen={showReviewRequestModal}
        onClose={() => setShowReviewRequestModal(false)}
        estimate={estimate}
        isRequesting={isRequestingReview}
        onSubmit={handleRequestReview}
      />

      {/* Low Confidence Warning Modal */}
      {showLowConfidenceWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4 text-amber-600">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                ⚠️
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Low Confidence Warning</h3>
                <p className="text-sm text-gray-600">Measurement may be inaccurate</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              This estimate has a low measurement confidence score ({estimate?.measurement_confidence?.score}%). 
              For accurate quotes, consider importing a professional measurement report before proceeding.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowLowConfidenceWarning(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={proceedWithAction}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                Proceed Anyway
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Measurement Override Modal */}
      {showMeasurementOverride && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Override Measurement</h3>
            <div className="space-y-4">
              <div>
                <Label>New Square Footage *</Label>
                <Input
                  type="number"
                  value={overrideSquareFootage}
                  onChange={(e) => setOverrideSquareFootage(e.target.value)}
                  placeholder="Enter square footage"
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <textarea
                  value={overrideNotes}
                  onChange={(e) => setOverrideNotes(e.target.value)}
                  className="w-full border rounded-lg p-2"
                  placeholder="Reason for override"
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={overrideVerified}
                  onChange={(e) => setOverrideVerified(e.target.checked)}
                />
                <span className="text-sm">Mark as verified measurement</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowMeasurementOverride(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleMeasurementOverride} disabled={isProcessing} className="flex-1 bg-blue-600">
                {isProcessing ? 'Updating...' : 'Update Measurement'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Eagleview Import Modal */}
      <EagleviewImportModal
        isOpen={showEagleviewImport}
        onClose={() => setShowEagleviewImport(false)}
        estimateId={estimateId}
        onSuccess={loadEstimate}
      />

      {/* Roofr Import Modal */}
      <RoofrImportModal
        isOpen={showRoofrImport}
        onClose={() => setShowRoofrImport(false)}
        estimateId={estimateId}
        onSuccess={loadEstimate}
      />

      {/* Additional Charges Modal */}
      <AdditionalChargesModal
        isOpen={showAdditionalCharges}
        onClose={() => setShowAdditionalCharges(false)}
        estimateId={estimateId}
        estimate={estimate}
        onSuccess={loadEstimate}
      />

      {/* Edit Customer Info Modal */}
      {showEditCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Customer Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <Input
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <Input
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <Input
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                  placeholder="Property address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <Input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="customer@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <Input
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowEditCustomerModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveCustomerInfo} disabled={isProcessing} className="flex-1 bg-blue-600">
                {isProcessing ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateDetail;
